import React, { useRef } from 'react';
import { LayoutGrid, Flame, TrendingUp, Sprout, Sparkles, Zap, FolderOpen, Rocket, Activity, DollarSign, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import DarkTooltip from '../ui/DarkTooltip';
import { getTierColor } from './utils';

/**
 * NicheFinderTabs — 11 abas horizontais com scroll em mobile + descricao
 * contextual abaixo (muda conforme a tab ativa).
 *
 * `counts` = stats do backend (objeto { total_active, filters: {...} })
 *
 * Pacote 4: sistema de tiers mutuamente exclusivos. `filter` casa com o
 * filter_type do backend (get_channels_filtered). Default = 'exploding'.
 * 'byCategory' tem endpoints proprios (CategoryGroupedViewCompact); 'all'
 * (Todos os Resultados) mostra tudo, sem exclusao de tier.
 */
const TAB_DEFS = [
  { id: 'exploding',        filter: 'exploding',        Icon: Flame,       labelKey: 'viralNiches.tab_exploding',        descKey: 'viralNiches.tab_exploding_desc'        },
  { id: 'rising_fast',      filter: 'rising_fast',      Icon: TrendingUp,  labelKey: 'viralNiches.tab_rising_fast',      descKey: 'viralNiches.tab_rising_fast_desc'      },
  { id: 'high_traction',    filter: 'high_traction',    Icon: Rocket,      labelKey: 'viralNiches.tab_high_traction',    descKey: 'viralNiches.tab_high_traction_desc'    },
  { id: 'strong_potential', filter: 'strong_potential', Icon: Sprout,      labelKey: 'viralNiches.tab_strong_potential', descKey: 'viralNiches.tab_strong_potential_desc' },
  { id: 'recent_traction',  filter: 'recent_traction',  Icon: Activity,    labelKey: 'viralNiches.tab_recent_traction',  descKey: 'viralNiches.tab_recent_traction_desc'  },
  { id: 'mature_monetized',      filter: 'mature_monetized',      Icon: DollarSign, labelKey: 'viralNiches.tab_mature_monetized',      descKey: 'viralNiches.tab_mature_monetized_desc'      },
  { id: 'established_monetized', filter: 'established_monetized', Icon: Trophy,     labelKey: 'viralNiches.tab_established_monetized', descKey: 'viralNiches.tab_established_monetized_desc' },
  { id: 'outlier',          filter: 'outlier',          Icon: Zap,         labelKey: 'viralNiches.tab_outlier',          descKey: 'viralNiches.tab_outlier_desc'          },
  { id: 'new_channels',     filter: 'new_channels',     Icon: Sparkles,    labelKey: 'viralNiches.tab_new_channels',     descKey: 'viralNiches.tab_new_channels_desc'     },
  { id: 'byCategory',       filter: 'all',              Icon: FolderOpen,  labelKey: 'viralNiches.tab_by_category',      descKey: 'viralNiches.by_category_short_desc'    },
  { id: 'all',              filter: 'all',              Icon: LayoutGrid,  labelKey: 'viralNiches.tab_all',              descKey: 'viralNiches.tab_all_desc'              },
];

// Cores das tabs ativas — DERIVADAS de TIER_COLORS (fonte unica, Pacote 7.2).
// 'all'/'byCategory' nao tem cor de tier -> usam o azul neutro do app.
// Alpha em hex de 8 digitos: bg ~10% (1A), border ~30% (4D), badge ~15% (26).
const TAB_NEUTRAL = '#0DAEFF';
function tabColors(tabId) {
  const c = getTierColor(tabId) || TAB_NEUTRAL;
  return {
    color: c,
    bg: `${c}1A`,
    border: `${c}4D`,
    badgeBg: `${c}26`,
  };
}

// visibleTabs (opcional): lista de ids para FILTRAR as abas exibidas — usado
// pelo modo TEASER (free/basic/lite), que mostra só os 7 tiers (sem
// 'byCategory'/'all'). Default undefined = todas as abas (comportamento atual).
const NicheFinderTabs = ({ activeTab, counts, onTabChange, visibleTabs }) => {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);

  const tabs = visibleTabs
    ? TAB_DEFS.filter((tab) => visibleTabs.includes(tab.id))
    : TAB_DEFS;

  const getCount = (id) => {
    if (!counts) return null;
    if (id === 'all') return counts.total_active ?? counts.filters?.all ?? null;
    if (id === 'byCategory') return null;
    return counts.filters?.[id] ?? null;
  };

  return (
    <div
      ref={scrollerRef}
      className="dp-thin-scrollbar"
      style={{
        display: 'flex',
        gap: 6,
        // overflow-x:auto + overflow-y:visible permite scroll horizontal
        // sem clipar a borda inferior do botao ativo (Sprint v3 fix).
        overflowX: 'auto',
        overflowY: 'visible',
        paddingTop: 2,
        paddingBottom: 6,
        marginBottom: 0,
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const isDisabled = !!tab.disabled;
        const count = getCount(tab.id);
        const { Icon } = tab;
        const activeColors = tabColors(tab.id);
        const button = (
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onTabChange(tab)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--dp-font)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              background: isActive ? activeColors.bg : 'transparent',
              color: isActive ? activeColors.color : 'var(--dp-text-secondary)',
              border: `0.5px solid ${isActive ? activeColors.border : 'transparent'}`,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              pointerEvents: isDisabled ? 'none' : 'auto',
              transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!isActive && !isDisabled) {
                e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive && !isDisabled) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <Icon size={14} />
            <span>{t(tab.labelKey)}</span>
            {count !== null && count !== undefined && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '0 6px',
                  borderRadius: 999,
                  background: isActive
                    ? activeColors.badgeBg
                    : 'var(--dp-border-subtle)',
                  color: isActive ? activeColors.color : 'var(--dp-text-tertiary)',
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
        if (isDisabled) {
          // Wrapper span — pointer-events:none no button bloqueia o
          // tooltip; o wrapper captura o mouseenter.
          return (
            <DarkTooltip key={tab.id} content={t('viralNiches.coming_soon')}>
              <span style={{ display: 'inline-block' }}>{button}</span>
            </DarkTooltip>
          );
        }
        // Tab habilitada — tooltip mostra descricao curta da tab
        // (mesma exibida no rodape da fileira, garante acesso pra
        //  users que nao notaram a frase contextual).
        return (
          <DarkTooltip key={tab.id} content={t(tab.descKey)} position="bottom">
            {button}
          </DarkTooltip>
        );
      })}
    </div>
  );
};

export { TAB_DEFS };
export default NicheFinderTabs;
